import { useEffect, useRef } from 'react';

export type AgentCommandStatus = 'running' | 'done' | 'error';

export type AgentCommandBlock = {
  id: string;
  command: string;
  status: AgentCommandStatus;
  output: string[];
  returncode?: number | null;
  phase?: string;
};

/** Strip ANSI color / cursor codes for readable chat cards */
export function stripAnsi(text: string): string {
  return String(text || '')
    .replace(/\u001b\[[0-9;?]*[a-zA-Z]/g, '')
    .replace(/\u001b\][^\u0007]*(?:\u0007|\u001b\\)/g, '')
    .replace(/\r/g, '');
}

type CardProps = {
  command: AgentCommandBlock;
  expanded: boolean;
  onToggle: () => void;
  onStop?: () => void;
  maxOutputLines?: number;
};

function Spinner() {
  return (
    <span
      className="inline-block w-3.5 h-3.5 border-2 border-slate-300 border-t-blue-600 dark:border-slate-600 dark:border-t-blue-400 rounded-full animate-spin shrink-0"
      aria-label="running"
    />
  );
}

export function AgentCommandCard({
  command: cmd,
  expanded,
  onToggle,
  onStop,
  maxOutputLines = 80,
}: CardProps) {
  const endRef = useRef<HTMLDivElement>(null);
  const lines = cmd.output.slice(-maxOutputLines);

  useEffect(() => {
    if (expanded && cmd.status === 'running') {
      endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [cmd.output.length, expanded, cmd.status]);

  const border =
    cmd.status === 'running'
      ? 'border-blue-300 dark:border-blue-700 bg-blue-50/80 dark:bg-blue-950/30'
      : cmd.status === 'error'
        ? 'border-red-200 dark:border-red-800 bg-red-50/70 dark:bg-red-950/25'
        : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60';

  return (
    <div className={`rounded-lg border shadow-sm overflow-hidden ${border}`}>
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-black/[0.03] dark:hover:bg-white/[0.04] transition-colors"
        >
          <span className="mt-0.5 shrink-0">
            {cmd.status === 'running' ? (
              <Spinner />
            ) : cmd.status === 'error' ? (
              <span className="text-red-500 text-sm font-bold leading-none">✗</span>
            ) : (
              <span className="text-emerald-600 dark:text-emerald-400 text-sm font-bold leading-none">✓</span>
            )}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wide font-sans font-semibold text-gray-500 dark:text-gray-400">
                {cmd.phase === 'validate' ? 'validate' : cmd.phase === 'cursor' ? 'shell' : 'command'}
              </span>
              {cmd.status === 'running' && (
                <span className="text-[10px] font-sans text-blue-600 dark:text-blue-400">běží…</span>
              )}
              {cmd.status !== 'running' && cmd.returncode != null && (
                <span
                  className={`text-[10px] font-mono ${
                    cmd.returncode === 0 ? 'text-emerald-600' : 'text-red-600'
                  }`}
                >
                  exit {cmd.returncode}
                </span>
              )}
            </div>
            <p className="mt-0.5 font-mono text-[12px] text-gray-800 dark:text-gray-100 break-all leading-snug">
              $ {cmd.command}
            </p>
          </div>
          <span className="text-gray-400 text-xs shrink-0 mt-0.5">{expanded ? '▾' : '▸'}</span>
        </button>
        {cmd.status === 'running' && onStop && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onStop();
            }}
            className="shrink-0 px-2.5 flex items-center justify-center text-red-600 hover:bg-red-100 dark:hover:bg-red-950/50 border-l border-blue-200 dark:border-blue-800"
            title="Zastavit tento krok (zruší agent run)"
            aria-label="Stop running command"
          >
            <span className="text-base font-bold leading-none">×</span>
          </button>
        )}
      </div>
      {expanded && (
        <div className="border-t border-gray-200/80 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 px-3 py-2">
          {lines.length === 0 ? (
            <p className="text-[11px] font-mono text-gray-400">
              {cmd.status === 'running'
                ? 'běží… streamuju výstup'
                : cmd.status === 'error'
                  ? `(exit ${cmd.returncode ?? '?'} — bez zachyceného výstupu)`
                  : '(bez výstupu)'}
            </p>
          ) : (
            <pre className="text-[11px] leading-relaxed font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
              {lines.join('\n')}
            </pre>
          )}
          <div ref={endRef} />
        </div>
      )}
    </div>
  );
}

export function commandsFromAgentLogs(logs: any[] | undefined | null): AgentCommandBlock[] {
  const out: AgentCommandBlock[] = [];
  let current: AgentCommandBlock | null = null;
  let seq = 0;

  for (const ev of logs || []) {
    if (!ev || ev.type !== 'command') continue;
    const cmd = String(ev.payload?.command || ev.message || '').trim() || '(command)';
    const state = String(ev.state || 'running');
    const phase = ev.payload?.phase as string | undefined;

    if (state === 'start') {
      seq += 1;
      const block: AgentCommandBlock = {
        id: `hist-${seq}-${cmd.slice(0, 24)}`,
        command: cmd,
        status: 'running',
        output: [],
        phase,
      };
      out.push(block);
      current = block;
      continue;
    }

    if (state === 'running') {
      const lineRaw = ev.payload?.line ?? ev.message;
      const line =
        lineRaw && !String(lineRaw).startsWith('$')
          ? stripAnsi(String(lineRaw))
          : null;
      const needsNew =
        current === null || (cmd !== '(command)' && current.command !== cmd);
      if (needsNew) {
        seq += 1;
        const block: AgentCommandBlock = {
          id: `hist-${seq}-${cmd.slice(0, 24)}`,
          command: cmd,
          status: 'running',
          output: [],
          phase,
        };
        out.push(block);
        current = block;
      }
      if (line && current !== null) {
        current.output.push(line);
        if (current.output.length > 400) {
          current.output = current.output.slice(-400);
        }
      }
      continue;
    }

    if (state === 'end') {
      const code = ev.payload?.returncode as number | null | undefined;
      const blob = ev.payload?.output as string | undefined;
      if (current === null) {
        seq += 1;
        const block: AgentCommandBlock = {
          id: `hist-${seq}-${cmd.slice(0, 24)}`,
          command: cmd,
          status: 'done',
          output: [],
          phase,
        };
        out.push(block);
        current = block;
      }
      if (current !== null) {
        if (typeof blob === 'string' && blob.trim()) {
          const lines = stripAnsi(blob)
            .split('\n')
            .map((l) => l.trimEnd())
            .filter(Boolean);
          if (current.output.length < 5 && lines.length > current.output.length) {
            current.output = lines.slice(-200);
          } else {
            current.output.push(...lines.slice(-200));
          }
        }
        current.returncode = code ?? null;
        current.status = code && Number(code) !== 0 ? 'error' : 'done';
        if (cmd !== '(command)') current.command = cmd;
      }
      current = null;
    }
  }
  return out.slice(-40);
}

/** Reduce WS command events into block list */
export function applyCommandEvent(
  prev: AgentCommandBlock[],
  ev: {
    state?: string;
    message?: string;
    payload?: {
      command?: string;
      line?: string;
      returncode?: number;
      phase?: string;
      output?: string;
    };
    seq?: number;
  }
): AgentCommandBlock[] {
  const state = ev.state || 'running';
  // Never treat a stdout line as the command name (running events set message=line)
  const cmd = String(
    ev.payload?.command ||
      (state === 'start' ? ev.message : '') ||
      ''
  ).trim() || '(command)';
  const phase = ev.payload?.phase;
  const next = [...prev];

  const findOrCreateRunning = () => {
    let idx = -1;
    if (cmd && cmd !== '(command)') {
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].status === 'running' && next[i].command === cmd) {
          idx = i;
          break;
        }
      }
    }
    if (idx < 0) {
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].status === 'running') {
          idx = i;
          break;
        }
      }
    }
    if (idx < 0) {
      next.push({
        id: `cmd-${Date.now()}-${next.length}`,
        command: cmd,
        status: 'running',
        output: [],
        phase,
      });
      idx = next.length - 1;
    }
    return idx;
  };

  if (state === 'start') {
    // Idempotent: duplicate start for the SAME running command
    const last = next[next.length - 1];
    if (last && last.status === 'running' && last.command === cmd) {
      return next;
    }
    for (let i = 0; i < next.length; i++) {
      if (next[i].status === 'running') {
        next[i] = {
          ...next[i],
          status: 'error',
          returncode: next[i].returncode ?? -1,
          output:
            next[i].output.length > 0
              ? next[i].output
              : ['[complic] interrupted — superseded by next command'],
        };
      }
    }
    next.push({
      id: `cmd-${Date.now()}-${next.length}`,
      command: cmd,
      status: 'running',
      output: [],
      phase,
    });
    return next.slice(-40);
  }

  if (state === 'running') {
    const idx = findOrCreateRunning();
    const raw = ev.payload?.line ?? (ev.message && ev.message !== cmd ? ev.message : null);
    if (raw) {
      const line = stripAnsi(String(raw));
      if (line.trim() && line !== cmd && !line.startsWith('$ ')) {
        const output = [...next[idx].output, line].slice(-400);
        next[idx] = {
          ...next[idx],
          command: cmd !== '(command)' ? cmd : next[idx].command,
          output,
          status: 'running',
        };
      }
    }
    return next.slice(-40);
  }

  if (state === 'end') {
    const idx = findOrCreateRunning();
    const code = ev.payload?.returncode;
    const blob = ev.payload?.output;
    let output = next[idx].output;
    if (typeof blob === 'string' && blob.trim()) {
      const lines = stripAnsi(blob)
        .split('\n')
        .map((l) => l.trimEnd())
        .filter((l) => l.length > 0);
      // Prefer full end blob when we had little/no live stream (docker buffer)
      if (output.length < 5 && lines.length > output.length) {
        output = lines.slice(-400);
      } else {
        output = [...output, ...lines].slice(-400);
      }
    }
    if (output.length === 0) {
      output = [
        code === 0
          ? '(bez výstupu — exit 0)'
          : `(exit ${code ?? '?'} — bez zachyceného výstupu)`,
      ];
    }
    next[idx] = {
      ...next[idx],
      command: cmd !== '(command)' ? cmd : next[idx].command,
      output,
      returncode: code ?? null,
      status: code && Number(code) !== 0 ? 'error' : 'done',
    };
    return next.slice(-40);
  }

  return next;
}
